/**
 * Reusable Table Component
 *
 * Features:
 * - Generic and type-safe with TypeScript
 * - Sorting capabilities (ascending/descending/none)
 * - Accessible with proper ARIA labels and keyboard navigation
 * - Responsive design with mobile-friendly features
 * - Uses existing design system tokens
 * - Pagination support (optional)
 */

import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Icon } from './Icon';
import { formatDateShort } from '../../utils/formatters';

// ============================================================================
// Types and Interfaces
// ============================================================================

export type SortDirection = 'asc' | 'desc' | null;

export interface Column<T> {
  key: keyof T | string;
  header: string;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, row: T) => React.ReactNode;
  ariaLabel?: string;
}

export interface TableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyExtractor: (item: T) => string | number;
  emptyMessage?: string;
  loading?: boolean;
  onSort?: (key: keyof T | string, direction: SortDirection) => void;
  initialSort?: {
    key: keyof T | string;
    direction: SortDirection;
  };
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border);
  background: var(--surface);
  box-shadow: none;

  /* Custom scrollbar for webkit browsers */
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: var(--surface-muted);
    border-radius: var(--radius-sm);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-strong);
    border-radius: var(--radius-sm);

    &:hover {
      background: var(--text-muted);
    }
  }

  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: var(--border-strong) var(--surface-muted);
`;

const TableElement = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);

  /* Mobile: allow table to be narrower, rely on horizontal scroll */
  @media (max-width: 767px) {
    font-size: var(--font-size-sm);
  }
`;

const TableHead = styled.thead`
  background: var(--surface-strong);
  position: sticky;
  top: 0;
  z-index: 10;

  /* Mobile: ensure thead displays properly with block table */
  @media (max-width: 767px) {
    display: table-header-group;
  }
`;

const TableHeaderCell = styled.th<{
  width?: string;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
}>`
  padding: var(--space-4) var(--space-3);
  text-align: ${({ align }) => align || 'left'};
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  border-bottom: 1px solid var(--border);
  position: relative;
  white-space: nowrap;

  ${({ width }) => width && `width: ${width}; min-width: ${width};`}

  ${({ sortable }) =>
    sortable &&
    `
    cursor: pointer;
    user-select: none;
    transition: background-color var(--transition-fast);

    &:hover {
      background: var(--interactive-hover);
    }

    &:focus {
      outline: 2px solid var(--interactive-focus);
      outline-offset: -2px;
    }
  `}

  /* Mobile: Reduced padding and better touch targets */
  @media (max-width: 767px) {
    padding: var(--space-3) var(--space-2);
    font-size: var(--font-size-xs);
    min-height: 48px; /* Touch target */
  }
`;

const SortButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--space-1);
  background: none;
  border: none;
  padding: var(--space-1);
  cursor: pointer;
  color: inherit;
  font: inherit;
  border-radius: var(--radius-sm);
  transition: background-color var(--transition-fast);
  min-height: 32px; /* Touch target for desktop */

  &:hover {
    background: var(--interactive-hover);
  }

  &:focus {
    outline: 2px solid var(--interactive-focus);
    outline-offset: -2px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  /* Mobile: Larger touch target for sort controls */
  @media (max-width: 767px) {
    padding: var(--space-2);
    min-height: 44px; /* Enhanced touch target */
    gap: var(--space-2);
  }
`;

const SortIcon = styled(Icon)<{ direction: SortDirection }>`
  transition: transform var(--transition-fast);
  color: ${({ direction }) =>
    direction ? 'var(--brand-600)' : 'var(--text-muted)'};

  ${({ direction }) => direction === 'desc' && 'transform: rotate(180deg);'}

  /* Mobile: Larger sort icon for better visibility */
  @media (max-width: 767px) {
    font-size: 1.1em;
  }
`;

const TableBody = styled.tbody`
  & > tr:nth-child(even) {
    background: var(--surface-subtle);
  }

  & > tr:hover {
    background: var(--interactive-hover);
  }

  & > tr:focus-within {
    background: var(--interactive-selected);
    outline: 2px solid var(--interactive-focus);
    outline-offset: -2px;
  }

  /* Mobile: ensure tbody displays properly with block table */
  @media (max-width: 767px) {
    display: table-row-group;
  }
`;

const TableRow = styled.tr`
  border-bottom: 1px solid var(--border);
  transition: background-color var(--transition-fast);

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td<{
  align?: 'left' | 'center' | 'right';
  width?: string;
}>`
  padding: var(--space-4) var(--space-3);
  text-align: ${({ align }) => align || 'left'};
  color: var(--text-secondary);
  vertical-align: middle;
  border-bottom: 1px solid var(--border-light);

  ${({ width }) => width && `width: ${width}; min-width: ${width};`}

  /* Truncate long text with ellipsis */
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* Mobile: Reduced padding and allow text wrapping */
  @media (max-width: 767px) {
    padding: var(--space-3) var(--space-2);
    white-space: normal;
    word-break: break-word;
    font-size: var(--font-size-xs);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: var(--space-12) var(--space-6);
  color: var(--text-muted);
  font-size: var(--font-size-base);
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: var(--space-4);
  opacity: 0.5;
`;

const LoadingRow = styled.tr`
  & > td {
    padding: var(--space-8) var(--space-3);
    text-align: center;
    color: var(--text-muted);
    font-style: italic;
  }
`;

// ============================================================================
// Component
// ============================================================================

export function Table<T extends Record<string, any>>({
  data,
  columns,
  keyExtractor,
  emptyMessage = 'No data available',
  loading = false,
  onSort,
  initialSort,
  className,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}: TableProps<T>) {
  const [sortKey, setSortKey] = useState<keyof T | string | null>(
    initialSort?.key || null,
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    initialSort?.direction || null,
  );

  // Handle sorting
  const handleSort = (key: keyof T | string) => {
    if (!columns.find((col) => col.key === key)?.sortable) return;

    let newDirection: SortDirection = 'asc';

    if (sortKey === key) {
      if (sortDirection === 'asc') {
        newDirection = 'desc';
      } else if (sortDirection === 'desc') {
        newDirection = null;
      }
    }

    setSortKey(newDirection ? key : null);
    setSortDirection(newDirection);

    // Call external sort handler if provided
    onSort?.(key, newDirection);
  };

  // Sort data if no external handler is provided
  const sortedData = useMemo(() => {
    if (!onSort && sortKey && sortDirection && data.length > 0) {
      return [...data].sort((a, b) => {
        const aValue = a[sortKey as keyof T];
        const bValue = b[sortKey as keyof T];

        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortDirection === 'asc' ? -1 : 1;
        if (bValue == null) return sortDirection === 'asc' ? 1 : -1;

        // Handle string comparison
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue);
          return sortDirection === 'asc' ? comparison : -comparison;
        }

        // Handle number comparison
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }

        // Handle date comparison
        if (
          aValue &&
          bValue &&
          typeof aValue === 'object' &&
          typeof bValue === 'object' &&
          'getTime' in aValue &&
          'getTime' in bValue
        ) {
          return sortDirection === 'asc'
            ? (aValue as Date).getTime() - (bValue as Date).getTime()
            : (bValue as Date).getTime() - (aValue as Date).getTime();
        }

        // Fallback to string comparison
        const aStr = String(aValue);
        const bStr = String(bValue);
        const comparison = aStr.localeCompare(bStr);
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return data;
  }, [data, sortKey, sortDirection, onSort]);

  // Render sort icon
  const renderSortIcon = (columnKey: keyof T | string) => {
    const isActive = sortKey === columnKey;
    const direction = isActive ? sortDirection : null;

    return (
      <SortIcon
        name={
          direction === 'asc'
            ? 'chevron-up'
            : direction === 'desc'
              ? 'chevron-down'
              : 'chevron-down'
        }
        size="sm"
        direction={direction}
        aria-hidden
      />
    );
  };

  // Render cell content
  const renderCellContent = (column: Column<T>, row: T) => {
    const value = row[column.key as keyof T];

    if (column.render) {
      return column.render(value, row);
    }

    // Default rendering
    if (value == null) {
      return <span style={{ color: 'var(--text-muted)' }}>—</span>;
    }

    if (typeof value === 'boolean') {
      return value ? 'Sí' : 'No';
    }

    if (value && typeof value === 'object' && 'getTime' in value) {
      return formatDateShort((value as Date).toISOString());
    }

    return String(value);
  };

  return (
    <TableContainer
      className={className}
      role="region"
      aria-label={ariaLabel || 'Tabla de datos'}
      tabIndex={0}
    >
      <TableElement
        role="table"
        aria-label={ariaLabel || 'Tabla de datos'}
        {...(ariaDescribedBy && { 'aria-describedby': ariaDescribedBy })}
      >
        <TableHead>
          <TableRow role="row">
            {columns.map((column) => (
              <TableHeaderCell
                key={String(column.key)}
                {...(column.width && { width: column.width })}
                {...(column.align && { align: column.align })}
                {...(column.sortable !== undefined && {
                  sortable: column.sortable,
                })}
                role="columnheader"
                aria-sort={
                  sortKey === column.key && sortDirection
                    ? sortDirection === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                }
              >
                {column.sortable ? (
                  <SortButton
                    type="button"
                    onClick={() => handleSort(column.key)}
                    aria-label={`${column.header}, ordenar ${
                      sortDirection === 'asc' ? 'descendente' : 'ascendente'
                    }`}
                  >
                    {column.header}
                    {renderSortIcon(column.key)}
                  </SortButton>
                ) : (
                  column.header
                )}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <LoadingRow>
              <td colSpan={columns.length} role="cell">
                <Icon name="loading" size="sm" aria-hidden /> Cargando datos...
              </td>
            </LoadingRow>
          ) : sortedData.length === 0 ? (
            <tr>
              <td colSpan={columns.length} role="cell">
                <EmptyState>
                  <EmptyIcon>
                    <Icon name="search" size="lg" aria-hidden />
                  </EmptyIcon>
                  {emptyMessage}
                </EmptyState>
              </td>
            </tr>
          ) : (
            sortedData.map((row) => (
              <TableRow key={keyExtractor(row)} role="row">
                {columns.map((column) => (
                  <TableCell
                    key={String(column.key)}
                    {...(column.width && { width: column.width })}
                    {...(column.align && { align: column.align })}
                    role="cell"
                    title={
                      typeof row[column.key as keyof T] === 'string'
                        ? (row[column.key as keyof T] as string)
                        : undefined
                    }
                  >
                    {renderCellContent(column, row)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </TableElement>
    </TableContainer>
  );
}

// ============================================================================
// Utility Components for Common Use Cases
// ============================================================================

export interface StatusBadgeProps {
  status: 'active' | 'inactive' | string;
  activeLabel?: string;
  inactiveLabel?: string;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  activeLabel = 'Activo',
  inactiveLabel = 'Inactivo',
}) => {
  const isActive = status === 'active' || status === 'true';
  const label = isActive ? activeLabel : inactiveLabel;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: 'var(--space-1) var(--space-2)',
        borderRadius: 'var(--radius-full)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        backgroundColor: isActive ? 'var(--success-100)' : 'var(--warning-100)',
        color: isActive ? 'var(--success-600)' : 'var(--warning-600)',
      }}
      aria-label={`Estado: ${label}`}
    >
      {label}
    </span>
  );
};

export interface BooleanIconProps {
  value: boolean;
  trueIcon?: string;
  falseIcon?: string;
  trueLabel?: string;
  falseLabel?: string;
}

export const BooleanIcon: React.FC<BooleanIconProps> = ({
  value,
  trueIcon = 'success',
  falseIcon = 'error',
  trueLabel = 'Sí',
  falseLabel = 'No',
}) => {
  const icon = value ? trueIcon : falseIcon;
  const label = value ? trueLabel : falseLabel;
  const color = value ? 'var(--success-600)' : 'var(--text-muted)';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        fontSize: 'var(--font-size-sm)',
        color: 'var(--text-secondary)',
      }}
      aria-label={label}
    >
      <Icon name={icon as any} size="sm" color={color} aria-hidden />
      <span>{label}</span>
    </div>
  );
};

export default Table;
